import "./App.css";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router";
import ReactGA from "react-ga4";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import Contact from "./pages/Contact/Contact";
import TermsOfService from "./pages/TermsOfService/TermsOfService";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import Mobile from "./pages/Mobile/Mobile";
import ChatRoom from "./pages/ChatRoom/ChatRoom";
import SearchResults from "./pages/SearchResults/SearchResults";
import Profile from "./pages/Profile/Profile";
import WatchPartiesPage from "./components/WatchParty/WatchPartiesPage";
import { useAppDispatch } from "./hooks";
import { useEffect } from "react";
import { setUser, clearUser, setProfileLoaded } from "./features/auth/authSlice";
import { supabase } from "./lib/supabaseClient";
import { PageTracker } from "./utils/usePageTracking";
import WatchPartyDetail from "./pages/WatchPartyDetail/WatchPartyDetail";
import Home from "./pages/Home/Home";

function Layout() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isChatRoom = location.pathname.startsWith("/chat/") || location.pathname.startsWith("/search");

  // Global auth logic - runs once on mount
  useEffect(() => {
    let mounted = true;
    let currentUserId: string | null = null;
    let profileFetchInProgress = false;

    // Helper function to handle user session
    const handleUserSession = async (user: any, skipInitialSet = false) => {
      if (!mounted || !user) return;

      // Prevent duplicate processing for the same user
      if (currentUserId === user.id && !skipInitialSet) {
        // console.log("Skipping duplicate user session processing for:", user.id);
        return;
      }

      // console.log("Processing user session for:", user.id);
      currentUserId = user.id;

      // Set user immediately to prevent loading screens
      // Profile data will be added asynchronously when available
      if (mounted) {
        dispatch(setUser(user));
        // console.log("User set in Redux immediately");
      }

      // Prevent duplicate profile fetches
      if (profileFetchInProgress) {
        // console.log("Profile fetch already in progress, skipping");
        return;
      }

      profileFetchInProgress = true;

      // Fetch profile data asynchronously (non-blocking)
      // Use a timeout to prevent hanging
      (async () => {
        try {
          // console.log("Fetching profile from user_profiles table...");

          const profilePromise = supabase.from("user_profiles").select("username, display_name, avatar_url").eq("id", user.id).single();

          const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Profile query timeout")), 5000));

          const { data: profile, error } = (await Promise.race([profilePromise, timeoutPromise])) as any;

          if (error) {
            if (error.code === "PGRST116") {
              // Profile doesn't exist - that's okay, user is already set
              // console.log("No profile found for user (PGRST116)");
            } else {
              // console.error("Error fetching profile:", error);
            }
            // Mark profile as loaded even if it doesn't exist
            if (mounted) dispatch(setProfileLoaded(true));
            // User is already set, no need to update
          } else if (profile && mounted) {
            // Successfully fetched profile - update user with profile data
            // console.log("Profile fetched successfully, updating user:", profile);
            dispatch(setUser({ ...user, ...profile }));
            // Profile is loaded (username will be set if it exists)
            dispatch(setProfileLoaded(true));
          }
        } catch (err: any) {
          // Catch timeout or other errors
          if (err?.message?.includes("timeout")) {
            // console.warn("Profile query timed out after 5s, user already set with basic data");
          } else {
            // console.error("Error fetching profile:", err);
          }
          // Mark profile as loaded even on error/timeout so modal doesn't flash
          if (mounted) dispatch(setProfileLoaded(true));
          // User is already set, so we can continue
        } finally {
          profileFetchInProgress = false;
        }
      })();
    };

    // Listen for auth state changes (this will fire with INITIAL_SESSION on mount)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      // console.log("Auth event:", event);

      if (session?.user) {
        // console.log("Session user:", session.user.id);
        // Skip initial set for INITIAL_SESSION to avoid duplicate processing
        // The INITIAL_SESSION event already provides the session
        await handleUserSession(session.user, event === "INITIAL_SESSION");
      } else {
        // console.log("No session, clearing user");
        currentUserId = null;
        profileFetchInProgress = false;
        dispatch(clearUser());
        dispatch(setProfileLoaded(false));
      }
    });

    return () => {
      mounted = false;
      currentUserId = null;
      profileFetchInProgress = false;
      subscription.unsubscribe();
    };
  }, [dispatch]);

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      {!isChatRoom && <Header />}
      <div className="flex-1 flex flex-col">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:username" element={<Profile />} />
          <Route path="/watch-parties" element={<WatchPartiesPage />} />
          <Route path="/watch-party/:partyId" element={<WatchPartyDetail />} />
          <Route path="/chat/:filmId" element={<ChatRoom />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/mobile" element={<Mobile />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {!isChatRoom && <Footer />}
    </div>
  );
}

function App() {
  ReactGA.initialize("G-NF7JJSGPQ4");

  return (
    <>
      <Router>
        <PageTracker />
        <Layout />
      </Router>
    </>
  );
}

export default App;
